import { includeNightsText } from "#app/utils/availabilityRequest.js";
import { formatDistanceToNowStrict, parseISO, parse } from "date-fns";
import { useState, useEffect } from "react";
import { formatMatchDate } from "#app/utils/dateFormat.js";
import { maybePluralizeNoun } from "#app/utils/words.js";

// Stable emoji per opening based on facility name + date
export const EMOJIS = [
  "\u26FA",
  "\uD83C\uDFD5\uFE0F",
  "\uD83C\uDFDE\uFE0F",
  "\uD83C\uDF32",
  "\uD83D\uDD25",
  "\uD83D\uDDFA\uFE0F",
  "\uD83C\uDF04",
  "\uD83E\uDD8C",
  "\uD83E\uDDA5",
  "\uD83D\uDEB6",
];

export function pickEmoji(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  return EMOJIS[Math.abs(hash) % EMOJIS.length];
}

// One shared 30s ticker for every AlertCard. The feed renders in two responsive
// slots (mobile + desktop), so the card count doubles; without this each card
// would spin up its own setInterval. Subscribers share a single timer that only
// runs while at least one card is mounted.
const tickListeners = new Set();
let tickTimer = null;

function subscribeTick(cb) {
  tickListeners.add(cb);
  if (tickTimer == null) {
    tickTimer = setInterval(() => {
      for (const listener of tickListeners) listener();
    }, 30000);
  }
  return () => {
    tickListeners.delete(cb);
    if (tickListeners.size === 0) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  };
}

export function useOpeningData(opening, locale) {
  const availableDate = parse(
    opening.availableDate.split("T")[0],
    "yyyy-MM-dd",
    new Date(),
  );
  const dateLabel = formatMatchDate(availableDate, locale);

  // Defer relative-time formatting to the client. Computing it during SSR
  // and again during hydration produces different strings (server "now" vs
  // client "now"), which trips React's hydration warning and visibly
  // re-renders. Empty string until mounted, then live-update every 30s.
  const [timeAgo, setTimeAgo] = useState("");
  useEffect(() => {
    const update = () =>
      setTimeAgo(
        formatDistanceToNowStrict(parseISO(opening.availableAt), {
          addSuffix: true,
        }),
      );
    update();
    return subscribeTick(update);
  }, [opening.availableAt]);

  const siteName = opening.site
    ? isNaN(opening.site.name)
      ? opening.site.name
      : `#${opening.site.name}`
    : null;
  const nights = includeNightsText({ siteType: opening.site?.type })
    ? maybePluralizeNoun(opening.availableLength, "night")
    : null;
  return { dateLabel, timeAgo, siteName, nights };
}
